import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
  message: string;
  children: React.ReactNode;
}

// Single-message counterpart to DynamicDataTooltip - that one renders a field/value grid from a
// data object, this one just needs one already-phrased sentence (e.g. "Agents associated to this
// plan do not require this patch") without a label glued in front of it.
const Tooltip: React.FC<TooltipProps> = ({ message, children }) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [position, setPosition] = useState<null | {
    left: number;
    top: number;
    placement: 'top' | 'bottom';
  }>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: 'top' | 'bottom' = spaceBelow < 100 ? 'top' : 'bottom';

    setPosition({
      left: rect.left + rect.width / 2,
      top: placement === 'bottom' ? rect.bottom + 12 : rect.top - 12,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      setAnimateIn(false);
      return;
    }

    const frame = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let frame = 0;
    const handleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => updatePosition());
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  if (!message) {
    return <>{children}</>;
  }

  const shouldRenderTooltip = isOpen || animateIn;

  const tooltip =
    !position || !shouldRenderTooltip ? null : (
      <div
        className={`
        pointer-events-none
        fixed
        z-[9999]
        flex
        -translate-x-1/2
        transform-gpu
        transition-all duration-200 ease-out
        ${animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        ${
          position.placement === 'bottom'
            ? animateIn
              ? 'translate-y-0'
              : '-translate-y-1'
            : animateIn
              ? '-translate-y-full'
              : '-translate-y-[calc(100%+4px)]'
        }
      `}
        style={{
          left: position.left,
          top: position.top,
        }}
      >
        <div
          className="
          relative
          overflow-hidden
          rounded-2xl
          border border-white/10
          bg-black/80
          backdrop-blur-xl
          shadow-[0_10px_40px_rgba(0,0,0,0.45)]
          px-4 py-3
          max-w-[280px]
        "
        >
          <div
            className="
            absolute inset-0
            bg-gradient-to-br
            from-white/10
            via-transparent
            to-transparent
            pointer-events-none
          "
          />

          <div
            className={`
            relative z-10
            text-xs font-medium leading-relaxed text-white text-center
            transition-all duration-300
            ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}
          `}
          >
            {message}
          </div>

          <div
            className="
            absolute inset-0
            rounded-2xl
            border border-white/5
          "
          />
        </div>

        <div
          className={
            position.placement === 'bottom'
              ? `
              absolute left-1/2 bottom-full
              h-3 w-3
              -translate-x-1/2 translate-y-1/2
              rotate-45
              border-l border-t border-white/10
              bg-black/80
              backdrop-blur-xl
            `
              : `
              absolute left-1/2 top-full
              h-3 w-3
              -translate-x-1/2 -translate-y-1/2
              rotate-45
              border-r border-b border-white/10
              bg-black/80
              backdrop-blur-xl
            `
          }
        />
      </div>
    );

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex cursor-help"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => {
        setAnimateIn(false);
        setTimeout(() => setIsOpen(false), 200);
      }}
      onFocus={() => setIsOpen(true)}
      onBlur={() => {
        setAnimateIn(false);
        setTimeout(() => setIsOpen(false), 200);
      }}
    >
      {children}
      {typeof document !== 'undefined' ? ReactDOM.createPortal(tooltip, document.body) : null}
    </div>
  );
};

export default Tooltip;
